/**
 * Lead Desk client contract: the shapes served by server/lead-desk-routes.ts, labels, and small fetch / format helpers.
 */
import type { Lead } from "@shared/schema";

export type Stage = "new" | "talking" | "booked" | "proposal" | "client" | "lost";
export type Fit = "strong" | "ok" | "weak" | "out_of_scope";
export type ListFilter = "all" | "needs_you" | "new" | "talking" | "booked" | "proposal" | "clients" | "lost" | "new_today" | "booked_week" | "clients_month";

export interface NextAction { kind: string; label: string; dueAt: string | null; emailId?: number; taskId?: number }
export interface LeadRow {
  id: number; name: string; email: string; phone: string | null; source: string;
  need: string | null; fit: Fit | null; fitScore: number | null; estimateText: string | null;
  stage: Stage; status: string; nextAction: NextAction | null; needsYou: boolean;
  lastContactAt: string | null; createdAt: string | null; optedOut: boolean; nurturePaused: boolean;
}
export interface Counts {
  newToday: number; needsYou: number; bookedThisWeek: number; clientsThisMonth: number;
  byStage: Record<Stage | "all", number>; chatsWithoutContact: number;
}
export interface LeadListResponse { rows: LeadRow[]; total: number; page: number; pageSize: number; filter: ListFilter; tz: string; counts: Counts; unassignedTasks?: OpenTask[] }

export interface ChatMessage { role: "user" | "assistant" | string; text: string; ts: string | null }
export interface ChatWithoutContact {
  id: number; visitorName: string | null; visitorPhone: string | null; pageUrl: string | null; need: string | null; step: string | null;
  startedAt: string | null; lastMessageAt: string | null; messageCount: number; preview: string | null; messages: ChatMessage[];
}
export interface ChatsResponse { rows: ChatWithoutContact[]; total: number; page: number; pageSize: number }

export type TimelineKind = "created" | "chat" | "email_out" | "message_in" | "message_out" | "appointment" | "callback" | "memory" | "note" | "rogers" | "task" | "follow_up_step";
export interface TimelineItem { id: string; at: string; kind: TimelineKind; title: string; body?: string | null; meta?: Record<string, any> }
export interface PendingEmail { id: number; subject: string; body: string | null; rawBody: string | null; hasHtml: boolean; status: string; queuedAt: string | null; to: string; kind?: string | null }
export interface OpenTask { id: number; kind: string; title: string; detail: string | null; dueAt: string | null; payload: Record<string, any> | null; createdAt: string }
export interface ConsentView { type: string; valid: boolean; legacy: boolean; expiresAt: string | null; detail: string; since: string | null; source: string | null; wording: string | null }
export type LeadDetail = Lead & {
  stage: Stage; fit: Fit | null; estimateText: string | null; need: string | null; needsYou: boolean; optedOut: boolean;
  suppression: { reason: string; source: string | null; since: string | null } | null; consent: ConsentView;
};
export interface TimelineResponse { lead: LeadDetail; pendingEmails: PendingEmail[]; openTasks: OpenTask[]; items: TimelineItem[] }

export const STAGE_LABEL: Record<Stage, string> = { new: "New", talking: "Talking", booked: "Booked", proposal: "Proposal sent", client: "Client", lost: "Lost" };
export const STAGE_ORDER: Stage[] = ["new", "talking", "booked", "proposal", "client", "lost"];
export const STAGE_STYLE: Record<Stage, string> = {
  new: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  talking: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  booked: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  proposal: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  client: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  lost: "bg-muted text-muted-foreground border-border",
};
export const FIT_LABEL: Record<Fit, string> = { strong: "Strong", ok: "OK", weak: "Weak", out_of_scope: "Out of scope" };
export const FIT_STYLE: Record<Fit, string> = {
  strong: "text-emerald-400",
  ok: "text-sky-400",
  weak: "text-amber-400",
  out_of_scope: "text-muted-foreground",
};
export const TASK_KIND_LABEL: Record<string, string> = {
  callback: "Call back", reply: "Reply", hand_off: "Hand-off", hot_lead: "Hot lead", follow_up: "Follow up", booking_issue: "Booking issue",
};

export const leadsKey = (wsId: number | null) => ["/api/workspaces", wsId, "lead-desk"] as const;
export const timelineKey = (wsId: number | null, leadId: number | null) => ["/api/workspaces", wsId, "lead-desk", "timeline", leadId] as const;

/** The server's own words for a failed call ({message} JSON, or the text), never "500: {...}". */
async function readError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => "");
  let msg = "";
  try { const j = JSON.parse(text); msg = j?.message || j?.error || ""; } catch { /* not json */ }
  if (!msg) msg = text && !/^\s*</.test(text) ? text.slice(0, 300) : `Request failed (${res.status})`;
  const e = new Error(msg) as Error & { status?: number };
  e.status = res.status;
  return e;
}

export async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw await readError(res);
  return res.json() as Promise<T>;
}

export async function sendJson<T = any>(method: "POST" | "PATCH" | "PUT" | "DELETE", url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method, credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await readError(res);
  const text = await res.text();
  try { return JSON.parse(text) as T; } catch { return text as unknown as T; }
}

export function relTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diff = t - now;
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60_000);
  const fmt = (n: number, u: string) => (diff < 0 ? `${n}${u} ago` : `in ${n}${u}`);
  if (mins < 1) return diff < 0 ? "just now" : "now";
  if (mins < 60) return fmt(mins, "m");
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return fmt(hrs, "h");
  const days = Math.round(hrs / 24);
  if (days < 7) return fmt(days, "d");
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: new Date(t).getFullYear() === new Date(now).getFullYear() ? undefined : "numeric" });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;
